import {
  Divider,
  Row,
  Typography,
  Input,
  Button,
  Table,
  Modal,
  Form,
  Col,
  message,
  Select,
  Menu,
  Dropdown,
  InputNumber,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { SearchOutlined, MoreOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import exportCSV from "assets/icons/exportcsv.svg";
import {
  disableSubmitButton,
  emailPattern,
  formatCSVData,
  formatNumberUS,
  servicesNamePattern,
  tenDigit,
  urlPattern,
} from "utility/constant";
import { CSVLink } from "react-csv";
import {
  CreateInfusionCenters,
  DeleteInfusionCenter,
  ListInfusionCenter,
} from "utility/action";
import search from "utility/search";
import { DeleteService } from "utility/DeleteService";
import { handleStatusChange } from "utility/StatusChange";
import SuccessModel from "utility/SuccessModel";
import DeleteModal from "utility/DeleteModal";
import TableSkeleton from "utility/TableSkeleton";

export default function InfusionCenter() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [icData, setICData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [successModel, setSuccessModel] = useState(false);
  const [dataSource, setDataSource] = useState(icData);
  const [csvData, setCsvData] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [deleteModelPrompt, setDeleteModelPrompt] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const { Option } = Select;

  const handleFormChange = disableSubmitButton(["name"])(
    form,
    setSubmitDisabled
  );

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
    setSuccessModel(false);
    setDeleteModelPrompt(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSuccessModel(false);
    setDeleteModelPrompt(false);
  };

  const setInfusionRow = function (row) {
    return {
      key: row?.id,
      searchByName: row?.searchByName,
      Action: row?.id,
      InfusionCenterName: row?.name,
      Email: row?.email,
      Phone: row?.phone,
      Website: row?.website,
      createdAt: row?.createdAt,
    };
  };

  const columns = [
    {
      title: "Infusion Center Name",
      dataIndex: "InfusionCenterName",
    },
    {
      title: "Infusion Center Website",
      dataIndex: "Website",
    },
    {
      title: "Email",
      dataIndex: "Email",
    },
    {
      title: "Phone Number",
      dataIndex: "Phone",
      render: (number) => {
        return formatNumberUS(number);
      },
    },

    {
      title: "",
      dataIndex: "Action",
      className: "actions",
      render: (id) => {
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key={1}>
                  <Link to="/infusion-center-details" state={{ id: id }}>
                    Edit
                  </Link>
                </Menu.Item>
                <Menu.Item key={2}>
                  <Link
                    rel="noopener noreferrer"
                    onClick={() => {
                      setDeleteModelPrompt(true);
                      setDeleteId(id);
                    }}
                  >
                    Delete
                  </Link>
                </Menu.Item>
              </Menu>
            }
            overlayClassName="download-btn"
            placement="bottomRight"
          >
            <MoreOutlined />
          </Dropdown>
        );
      },
    },
  ];

  const fetchptCenter = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ListInfusionCenter(999);
      const data = response.map((row) => setInfusionRow(row));
      data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setICData(data);
      setDataSource(data);
      setCsvData(response);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchptCenter();
  }, [fetchptCenter]);

  const createInfusionCenter = async (values) => {
    try {
      setDeleteId("");
      setCreateLoading(true);
      const inputData = {
        name: values?.name,
        searchByName: (values?.name).toLowerCase(),
        email: values?.email,
        phone: values?.phone,
        website: values?.website,
        activeOnPortal: false,
      };
      const response = await CreateInfusionCenters(inputData);

      const mappedData = {};
      Object.entries(response).forEach(([key, value]) => {
        if (value !== null) {
          mappedData[key] = value;
        }
      });

      const tableData = {
        InfusionCenterName: mappedData?.name,
        Action: mappedData?.id,
        searchByName: mappedData?.searchByName,
        Email: mappedData?.email,
        Phone: mappedData?.phone,
        Website: mappedData?.website,
        createdAt: mappedData?.createdAt,
      };

      const sortByCreatedAt = (data) =>
        data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setICData((previous) => sortByCreatedAt([...previous, tableData]));
      setDataSource((previous) => sortByCreatedAt([...previous, tableData]));

      setIsModalOpen(false);
      form.resetFields();
      setSuccessModel(true);
      setTimeout(() => {
        setSuccessModel(false);
      }, 3000);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
      console.log(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSearch = async (e) => {
    const currValue = e.target.value.trim();
    const searchFields = ["InfusionCenterName", "Website", "Phone", "Email"];
    const filteredData = search(icData, currValue, searchFields);
    setDataSource(filteredData);
  };

  const labelKeyMap = {
    "Infusion Center Name": "name",
    Email: "email",
    Phone: "phone",
    Website: "website",
    About: "about",
    "Street Address": "streeAddress",
    City: "city",
    State: "state",
    "Zip Code": "zip",
    "Google Rating": "googleRating",
    "Facebook Rating": "facebookRating",
    "Google Map Link": "googleMapLink",
  };

  const { headers, resData } = formatCSVData(labelKeyMap, csvData);

  const handleChange = async (value) => {
    try {
      setSelectedValue(value);
      setLoading(true);
      const filterData = await handleStatusChange(
        value,
        setInfusionRow,
        ListInfusionCenter,
        icData
      );
      setDataSource(filterData);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const deletePtCenter = async () => {
    try {
      setDeleteLoading(true);
      await DeleteService(deleteId, DeleteInfusionCenter);
      setDeleteModelPrompt(false);
      setSuccessModel(true);
      setSelectedValue(null);
      fetchptCenter();
      setTimeout(() => {
        setSuccessModel(false);
      }, 3000);
    } catch (error) {
      console.log(error);
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Typography.Title level={4} className="text-center mb-0">
        Infusion Center
      </Typography.Title>
      <Divider />

      <Row justify="space-between table-header">
        <Row align="middle">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search"
            className="searchbar"
            onChange={handleSearch}
            style={{ width: 250 }}
          />
        </Row>

        <Row className="gap-10 mtr-20 bold-font filter-dropdown-list">
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 100 }}
            onChange={handleChange}
            value={selectedValue}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            <Option value="ACTIVE">ACTIVE</Option>
            <Option value="INACTIVE">INACTIVE</Option>
          </Select>
          <CSVLink
            data={resData}
            headers={headers}
            filename={"Infusion Center.csv"}
            className="export-btn"
          >
            <img src={exportCSV} alt="" className="action-icons" />
          </CSVLink>
          <Button className="blue-btn inherit-btn" onClick={showModal}>
            Add New
          </Button>
        </Row>
      </Row>

      {loading ? (
        <TableSkeleton />
      ) : (
        <Table
          columns={columns}
          dataSource={dataSource}
          className="common-table"
          scroll={{ x: 992 }}
          pagination={{ position: ["", "bottomCenter"] }}
        />
      )}

      <Modal
        className="form-modal"
        centered
        width={700}
        footer={null}
        title={
          <Typography.Title level={3} className="modal-title">
            Add Infusion Center
          </Typography.Title>
        }
        visible={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Form
          name="basic"
          form={form}
          onFinish={createInfusionCenter}
          autoComplete="off"
          layout="vertical"
          onFieldsChange={handleFormChange}
        >
          <Row gutter={20}>
            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Infusion center name"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Required!",
                  },
                  {
                    pattern: servicesNamePattern,
                    message: "Please enter a valid name",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Infusion center website"
                name="website"
                rules={[
                  {
                    pattern: urlPattern,
                    message: "Please input valid url",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Phone number"
                name="phone"
                rules={[
                  {
                    pattern: tenDigit,
                    message: "Please enter a valid phone number",
                  },
                ]}
              >
                <InputNumber
                  controls={false}
                  formatter={(value) => (value ? formatNumberUS(value) : "")}
                  parser={(value) => {
                    const cleanedValue = value.replace(/\D/g, "");
                    return cleanedValue.slice(0, 10);
                  }}
                  onChange={(e) => {}}
                  style={{ width: "100%", borderRadius: "5px" }}
                />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  {
                    pattern: emailPattern,
                    message: "Please enter a valid email",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={24} xs={24} sm={24} className="center-btn">
              <Button
                className="theme-btn"
                htmlType="submit"
                loading={createLoading}
                disabled={submitDisabled}
              >
                Submit
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      <SuccessModel
        message={
          deleteId !== ""
            ? "Infusion Center Deleted Successfully"
            : "Infusion Center Added Successfully"
        }
        open={successModel}
      />

      <DeleteModal
        content="Are you sure you want to delete the infusion center?"
        open={deleteModelPrompt}
        onClick={deletePtCenter}
        buttonLoading={deleteLoading}
        onOk={handleOk}
        onCancel={handleCancel}
        buttonText="Yes"
      />
    </>
  );
}
